import React, {Component} from "react";
import '../modals/AddDevice/AddDeviceModal.scss';
import {Dropdown, Input} from "gd-react";
import GridDuck from 'gridduck';

// Serial parity options understood by the Modbus RTU gateway. The first,
// empty-value option leaves the parity unset so the modbus model's own value
// is used.
const parityOptions = [
    {value: '', title: 'Use model default'},
    {value: 'none', title: 'None'},
    {value: 'odd', title: 'Odd'},
    {value: 'even', title: 'Even'},
    {value: 'mark', title: 'Mark'},
    {value: 'space', title: 'Space'}
];

// Native Modbus TCP devices ignore the unit id and are conventionally documented
// as 255; someone fronting a TCP-to-RTU gateway overwrites it with the real slave id.
const DEFAULT_TCP_UNIT_ID = 255;
const DEFAULT_TCP_PORT = 502;

// Four dot-separated octets, each 0-255. Matches the server's z.ipv4() closely
// enough to catch the typo before the request rather than after a 400.
const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

/**
 * Modbus connection settings, used both when adding a device (DeviceForm) and
 * when editing one (AssetSettingsDetails). Handles both transports: RTU over
 * RS-485 and TCP over the network. They share a register map (the modbus model)
 * and differ only in how the gateway reaches the device, which is why they also
 * share one asset column, discriminated by modbus_details.transport.
 *
 * Owns its own state. The parent renders it via a ref and calls:
 *  - validate()   -> boolean, flags errors when invalid
 *  - getValues()  -> {modbusId, assetIndex, modbusDetails} for the new asset
 *
 * Props:
 *  - asset            the asset being added/edited; its sku picks the transport
 *  - initialModbusId  existing modbus model id to pre-select (edit mode)
 *  - initialDetails   existing modbus_details to pre-fill (edit mode)
 *  - onChange         called whenever the user edits a field
 *  - disabled         render the form read-only
 *  - hideHeader       skip the internal divider + title (when a card header exists)
 *
 * The address maps to the asset's asset_index - the slave id on RTU, the unit id
 * on TCP. Parity and baud rate (RTU) and host and port (TCP) live in modbus_details.
 *
 * getValues() must return ONLY the keys belonging to the chosen transport: the
 * edit path sends modbus_details through as-is and the server validates it with a
 * strict discriminated union, so a stray parity on a TCP device is a 400 rather
 * than an ignored field.
 */
class ModbusDeviceDetails extends Component {

    constructor(props) {
        super(props);
        this.errors = {};
        this.state = {
            triedToSave: false,
            modbusModels: [],
            modbus: this.normaliseInitial(props.initialModbusId, props.initialDetails)
        };
        ['fetchModbusModels', 'handleChange', 'notifyChange', 'checkErrors', 'validate',
            'getValues', 'isTcp'].forEach((func) => this[func] = this[func].bind(this));
    }

    // The saved transport wins: assetDao reads sku back as `row.sku || third_party_type`,
    // so an edited TCP device arrives with sku 'MODBUS'. Sku is the fallback when adding.
    isTcp() {
        let transport = (this.props.initialDetails || {}).transport;
        if (transport) return transport === 'tcp';
        let sku = this.props.asset && this.props.asset.sku;
        return sku === 'MODBUS_TCP';
    }

    normaliseInitial(modbusId, initial) {
        // baud_rate is stored snake_case, but the SDK model caches it as baudRate
        // in-memory right after a save, so accept either casing when re-seeding.
        let baudRate = initial ? (initial.baud_rate != null ? initial.baud_rate : initial.baudRate) : undefined;
        // Cannot call this.isTcp() here - it runs before state exists - so mirror its
        // precedence exactly: saved transport first, sku only when there is none.
        let sku = this.props.asset && this.props.asset.sku;
        let transport = (initial || {}).transport;
        let tcp = transport ? transport === 'tcp' : sku === 'MODBUS_TCP';
        let address = initial && initial.address != null ? initial.address
            : (tcp ? DEFAULT_TCP_UNIT_ID : '');
        return {
            modbusId: modbusId || '',
            address: address,
            parity: (initial && initial.parity) || '',
            baudRate: baudRate != null ? baudRate : '',
            host: (initial && initial.host) || '',
            port: initial && initial.port != null ? initial.port : (tcp ? DEFAULT_TCP_PORT : '')
        };
    }

    componentDidMount() {
        this.fetchModbusModels();
    }

    async fetchModbusModels() {
        let res = await GridDuck.getModbusModels({filters: []});
        let modbusModels = res.list.map((m) => ({value: m.id, title: m.displayName}));
        this.setState({modbusModels: modbusModels});
    }

    notifyChange() {
        if (this.props.onChange) this.props.onChange();
    }

    handleChange(input, field) {
        let modbus = this.state.modbus;
        modbus[field] = input;
        this.setState({modbus: modbus, triedToSave: false});
        this.notifyChange();
    }

    checkErrors() {
        let errors = {};
        let m = this.state.modbus;
        let isNotNumber = (val) => val === '' || val === null || val === undefined || isNaN(Number(val));
        if (!m.modbusId) errors['modbusId'] = true;
        if (this.isTcp()) {
            // 0-255, not the RTU 1-247: a native TCP device ignores the unit id and is
            // usually documented as 255, which the RTU range would reject.
            if (isNotNumber(m.address) || Number(m.address) < 0 || Number(m.address) > 255) errors['address'] = true;
            if (!m.host || !IPV4.test(String(m.host).trim())) errors['host'] = true;
            if (isNotNumber(m.port) || Number(m.port) < 1 || Number(m.port) > 65535) errors['port'] = true;
        } else {
            if (isNotNumber(m.address) || Number(m.address) < 1 || Number(m.address) > 247) errors['address'] = true;
            if (m.baudRate !== '' && (isNotNumber(m.baudRate) || Number(m.baudRate) <= 0)) errors['baudRate'] = true;
        }
        this.errors = errors;
    }

    // Called by the parent (via ref) before saving. Returns true when valid.
    validate() {
        this.checkErrors();
        this.setState({triedToSave: true});
        return Object.keys(this.errors).length === 0;
    }

    // Called by the parent (via ref) to read the values for the new asset.
    getValues() {
        let m = this.state.modbus;
        let tcp = this.isTcp();
        // Only this transport's keys: the server's schema is strict, so anything
        // left over from the other transport fails the save.
        let modbusDetails = {transport: tcp ? 'tcp' : 'rtu'};
        if (tcp) {
            modbusDetails.host = String(m.host).trim();
            modbusDetails.port = parseInt(m.port);
        } else {
            if (m.parity) modbusDetails.parity = m.parity;
            if (m.baudRate !== '') modbusDetails.baudRate = parseInt(m.baudRate);
        }
        return {
            modbusId: m.modbusId,
            assetIndex: parseInt(m.address),
            modbusDetails: modbusDetails
        };
    }

    render() {
        this.checkErrors();
        let self = this;
        let m = this.state.modbus;
        let tcp = this.isTcp();
        let triedToSave = this.state.triedToSave;
        let disabled = this.props.disabled;
        return (
            <div>
                {!this.props.hideHeader ?
                    <div>
                        <div className={'add-device-divider'}/>
                        <div className={'row title-wrapper'} style={{marginBottom: '-10px'}}>
                            <div className={'text-wrapper'}>
                                <p>{tcp ? 'Modbus TCP Connection' : 'Modbus RTU Connection'}</p>
                            </div>
                        </div>
                    </div> : null}
                <div className={'row'} style={{marginTop: '14px'}}>
                    <Dropdown
                        label={'Modbus Model'}
                        name={'modbus-model'}
                        disabled={disabled}
                        error={triedToSave && this.errors['modbusId']}
                        value={m.modbusId}
                        fixeditems={this.state.modbusModels}
                        placeholder={'Select a modbus model'}
                        onChange={(input) => self.handleChange(input.target.value, 'modbusId')}
                    />
                </div>
                {tcp ?
                    <div className={'row'} style={{marginTop: '14px'}}>
                        <Input
                            label={'Host IP Address'}
                            name={'modbus-host'}
                            disabled={disabled}
                            error={triedToSave && this.errors['host']}
                            value={m.host}
                            placeholder={'e.g. 192.168.1.50'}
                            onChange={(input) => self.handleChange(input.target.value, 'host')}
                        />
                    </div> : null}
                {tcp ?
                    <div className={'row'} style={{marginTop: '14px'}}>
                        <Input
                            label={'Port'}
                            name={'modbus-port'}
                            type={'number'}
                            disabled={disabled}
                            error={triedToSave && this.errors['port']}
                            value={m.port}
                            placeholder={'e.g. 502'}
                            onChange={(input) => self.handleChange(input.target.value, 'port')}
                        />
                    </div> : null}
                <div className={'row'} style={{marginTop: '14px'}}>
                    <Input
                        label={tcp ? 'Unit ID' : 'Address (Slave ID)'}
                        name={'modbus-address'}
                        type={'number'}
                        disabled={disabled}
                        error={triedToSave && this.errors['address']}
                        value={m.address}
                        placeholder={tcp ? 'e.g. 255' : 'e.g. 1'}
                        onChange={(input) => self.handleChange(input.target.value, 'address')}
                    />
                </div>
                {!tcp ?
                    <div className={'row'} style={{marginTop: '14px'}}>
                        <Dropdown
                            label={'Parity'}
                            name={'modbus-parity'}
                            disabled={disabled}
                            value={m.parity}
                            fixeditems={parityOptions}
                            placeholder={'Use model default'}
                            onChange={(input) => self.handleChange(input.target.value, 'parity')}
                        />
                    </div> : null}
                {!tcp ?
                    <div className={'row'} style={{marginTop: '14px'}}>
                        <Input
                            label={'Baud Rate'}
                            name={'modbus-baud-rate'}
                            type={'number'}
                            disabled={disabled}
                            error={triedToSave && this.errors['baudRate']}
                            value={m.baudRate}
                            placeholder={'Use model default (e.g. 9600)'}
                            onChange={(input) => self.handleChange(input.target.value, 'baudRate')}
                        />
                    </div> : null}
            </div>
        );
    }
}

export default ModbusDeviceDetails;
